<script setup lang="ts">
import {
  type ContractManagementExtractionInDb,
  type ContractManagementExtractionOverwriteCreate,
  type ContractManagementExtractionOverwriteUpdate,
  type ContractManagementViewColumnInDb,
  type EnumResponseType,
  type GaiaJsonValue,
  createContractManagementExtractionOverwrite,
  updateContractManagementExtractionOverwrite,
} from '@/api';
import { isValueOverwritten } from '@/utils/overwrite';
import { IconUser } from '@/components/design/icons';
import { computed, ref } from 'vue';
import GaiaExtractionFieldRow from '@/components/design/domain/extraction/GaiaExtractionFieldRow.vue';
import type { ExtractedField, FieldType } from '@/types/evaluation';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  show: boolean;
  cellData: {
    columnUid: string;
    extraction: ContractManagementExtractionInDb | null;
    column: ContractManagementViewColumnInDb | null;
    contractUid?: string;
    fileUid?: string;
  };
  onDataUpdate?: () => Promise<void>;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:show': [value: boolean];
}>();

const message = useGaiaToast();

// Edit state
const editMode = ref<boolean>(false);

// Collect all edit values from child components
const allEditValues = ref<
  Map<
    string,
    {
      value: GaiaJsonValue;
      reason: string;
      page: string;
      highlighted_text: string[];
      evaluation: string;
      evaluation_reason: string;
    }
  >
>(new Map());

// Define the fields to display
const fieldList = computed(() => {
  if (!props.cellData.extraction) return [];

  const extraction = props.cellData.extraction;
  const overwrite = extraction.human_overwrite ?? null;

  return [
    {
      key: 'ai_value',
      label: isValueOverwritten(overwrite?.value, extraction.value) ? 'Value' : 'AI Response',
      value: overwrite?.value === undefined ? extraction.value : overwrite.value,
    },
    {
      key: 'ai_reason',
      label: isValueOverwritten(overwrite?.reason, extraction.reason) ? 'Reason' : 'AI Reason',
      value: overwrite?.reason === undefined ? extraction.reason : overwrite.reason,
    },
    {
      key: 'ai_page',
      label: isValueOverwritten(overwrite?.page, extraction.page) ? 'Page' : 'AI Page',
      value: overwrite?.page === undefined ? extraction.page : overwrite.page,
    },
    {
      key: 'ai_highlighted_text',
      label: isValueOverwritten(overwrite?.highlighted_text, extraction.highlighted_text)
        ? 'Highlighted Text'
        : 'AI Highlighted Text',
      value:
        overwrite?.highlighted_text === undefined
          ? extraction.highlighted_text
          : overwrite.highlighted_text,
    },
  ];
});

// Create field meta object for FieldDisplay component
function getFieldMeta(fieldKey: string): ExtractedField {
  if (!props.cellData.extraction) {
    throw new Error('Extraction not available');
  }

  const extraction = props.cellData.extraction;
  const overwrite = extraction.human_overwrite ?? null;

  // Determine field type: prefer column question, fallback to extraction
  // The extraction.response_type is the actual type from the endpoint
  // Can be: datetime, str, float, number, int, bool, list[...], CurrencyAmount,
  // list[CurrencyAmount], Entity, list[Entity], CalendarEvents, or EnumResponseType
  let fieldType: FieldType = 'str';
  let enumOptions: EnumResponseType | undefined = undefined;

  // Check the column's extraction prompt first (most authoritative for field definition)
  if (props.cellData.column) {
    const columnResponseType = props.cellData.column.extraction_prompt.response_type;
    // Handle both string types and EnumResponseType objects
    if (typeof columnResponseType === 'string') {
      fieldType = columnResponseType;
    } else if (typeof columnResponseType === 'object' && 'values' in columnResponseType) {
      // It's an EnumResponseType object
      fieldType = columnResponseType;
      enumOptions = columnResponseType;
    }
  }
  // Fallback to extraction response_type (actual type from endpoint)
  else {
    // Handle both string types and EnumResponseType objects
    if (typeof extraction.response_type === 'string') {
      fieldType = extraction.response_type;
    } else if (
      typeof extraction.response_type === 'object' &&
      'values' in extraction.response_type
    ) {
      // It's an EnumResponseType object
      fieldType = extraction.response_type;
      enumOptions = extraction.response_type;
    }
  }

  // If we still don't have enum options, check extraction even if we got fieldType from column
  // This ensures we have enum options even if column question doesn't provide them
  if (
    !enumOptions &&
    typeof extraction.response_type === 'object' &&
    'values' in extraction.response_type
  ) {
    enumOptions = extraction.response_type;
  }

  // Check if each field has been overwritten (not undefined)
  const hasValueOverwrite = overwrite?.value !== undefined;
  const hasReasonOverwrite = overwrite?.reason !== undefined;
  const hasPageOverwrite = overwrite?.page !== undefined;
  const hasHighlightedTextOverwrite = overwrite?.highlighted_text !== undefined;

  return {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    field: props.cellData.column?.extraction_prompt.prompt || fieldKey,
    field_type: fieldType,
    ai_value: extraction.value,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
    ai_reason: extraction.reason || null,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
    ai_page: extraction.page || null,
    ai_highlighted_text: extraction.highlighted_text ?? null,
    ai_evaluation: null, // Contract management extractions don't have evaluation
    has_value_overwrite: hasValueOverwrite,
    has_reason_overwrite: hasReasonOverwrite,
    has_page_overwrite: hasPageOverwrite,
    has_highlighted_text_overwrite: hasHighlightedTextOverwrite,
    has_evaluation_overwrite: false,
    has_evaluation_reason_overwrite: false,
    overwritten_value: hasValueOverwrite ? overwrite.value : null,
    overwritten_reason: hasReasonOverwrite ? (overwrite.reason ?? null) : null,
    overwritten_page: hasPageOverwrite ? (overwrite.page ?? null) : null,
    overwritten_highlighted_text: hasHighlightedTextOverwrite
      ? (overwrite.highlighted_text ?? null)
      : null,
    overwritten_evaluation: null,
    overwritten_evaluation_reason: null,
    processing_state: extraction.processing_state,
    approved: extraction.approved,
    enum_options: enumOptions,
  };
}

// Handle edit values updates from child components
function handleEditValuesUpdate(
  key: string,
  values: {
    value: GaiaJsonValue;
    reason: string;
    page: string;
    highlighted_text: string[];
    evaluation: string;
    evaluation_reason: string;
  }
): void {
  allEditValues.value.set(key, values);
}

function enterEditMode(): void {
  editMode.value = true;
}

// Handle bulk submission of all changes
async function handleBulkSubmit(): Promise<void> {
  if (allEditValues.value.size === 0) {
    message.warning('No changes to submit');
    return;
  }

  if (!props.cellData.extraction) {
    message.error('Extraction not found');
    return;
  }

  try {
    const existingOverwrite = props.cellData.extraction.human_overwrite ?? null;
    const extractionUid = props.cellData.extraction.uid;

    // Build payload for contract management extraction overwrite
    // Note: Contract management overwrites don't have evaluation fields,
    // but they have overwrite_reason instead
    const payload:
      ContractManagementExtractionOverwriteCreate | ContractManagementExtractionOverwriteUpdate =
      {};

    // When updating, preserve existing values that aren't being edited
    if (existingOverwrite) {
      // Preserve existing values for fields not being edited
      if (!allEditValues.value.has('ai_value')) {
        payload.value = existingOverwrite.value;
      }
      if (!allEditValues.value.has('ai_reason')) {
        payload.reason = existingOverwrite.reason;
      }
      if (!allEditValues.value.has('ai_page')) {
        payload.page = existingOverwrite.page;
      }
      if (!allEditValues.value.has('ai_highlighted_text')) {
        payload.highlighted_text = existingOverwrite.highlighted_text;
      }
      // Preserve overwrite_reason
      payload.overwrite_reason = existingOverwrite.overwrite_reason;
    }

    // Process each edited field and override preserved values
    for (const [key, values] of allEditValues.value) {
      switch (key) {
        case 'ai_value':
          // The shared editor emits `GaiaJsonValue` (unknown), shaped by the column's response type.
          payload.value = values.value;
          break;
        case 'ai_reason':
          payload.reason = values.reason || null;
          break;
        case 'ai_page':
          payload.page = values.page ? Number(values.page) : null;
          break;
        case 'ai_highlighted_text':
          payload.highlighted_text =
            values.highlighted_text.length > 0 ? values.highlighted_text : null;
          break;
        // Note: Contract management doesn't have evaluation fields
        // evaluation and evaluation_reason are ignored for contract management
        default:
          break;
      }
    }

    // Call appropriate API endpoint
    if (existingOverwrite) {
      // Update existing overwrite
      const response = await updateContractManagementExtractionOverwrite({
        path: {
          contract_management_extraction_uid: extractionUid,
        },
        body: payload,
      });

      if (!response.data) {
        throw new Error('Failed to update extraction overwrite');
      }
    } else {
      // Create new overwrite
      const response = await createContractManagementExtractionOverwrite({
        path: {
          contract_management_extraction_uid: extractionUid,
        },
        body: payload,
      });

      if (!response.data) {
        throw new Error('Failed to create extraction overwrite');
      }
    }

    message.success('All changes submitted successfully');
    editMode.value = false;
    allEditValues.value.clear();
    await props.onDataUpdate?.();
  } catch (error) {
    logger.error('Error submitting changes:', error);
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to submit changes. Please try again.';
    message.error(errorMessage);
  }
}

function handleCancelEdit(): void {
  editMode.value = false;
  allEditValues.value.clear();
  // Don't refresh data on cancel - no changes were made
}

function handleModalUpdate(show: boolean): void {
  if (!show) {
    // Reset edit mode when modal closes
    editMode.value = false;
    allEditValues.value.clear();
    // Don't refresh data when modal is closed - only refresh after successful save
  }

  emit('update:show', show);
}

const hasExtraction = computed(() => Boolean(props.cellData.extraction));
</script>

<template>
  <NModal :show="show" @update:show="handleModalUpdate">
    <NCard style="width: 700px" :bordered="false" size="huge" role="dialog" aria-modal="true">
      <template #header>
        <div class="modal-header">
          <span>Extraction Details</span>
          <div class="header-actions">
            <NButton
              v-if="!editMode && hasExtraction"
              size="small"
              type="primary"
              @click="enterEditMode"
            >
              Edit
            </NButton>
            <div v-else-if="hasExtraction" class="edit-actions">
              <NButton size="small" type="primary" @click="handleBulkSubmit"> Submit </NButton>
              <NButton size="small" @click="handleCancelEdit"> Cancel </NButton>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!hasExtraction" class="no-extraction">
        <p>No extraction data available for this cell.</p>
      </div>

      <div v-else class="cell-details">
        <div v-for="field in fieldList" :key="field.key" class="detail-row">
          <div class="detail-label">
            <span class="label-text">{{ field.label }}</span>
            <NTooltip
              v-if="
                (field.key === 'ai_value' &&
                  isValueOverwritten(
                    cellData.extraction?.human_overwrite?.value,
                    cellData.extraction?.value
                  )) ||
                (field.key === 'ai_reason' &&
                  isValueOverwritten(
                    cellData.extraction?.human_overwrite?.reason,
                    cellData.extraction?.reason
                  )) ||
                (field.key === 'ai_page' &&
                  isValueOverwritten(
                    cellData.extraction?.human_overwrite?.page,
                    cellData.extraction?.page
                  )) ||
                (field.key === 'ai_highlighted_text' &&
                  isValueOverwritten(
                    cellData.extraction?.human_overwrite?.highlighted_text,
                    cellData.extraction?.highlighted_text
                  ))
              "
            >
              <template #trigger>
                <NIcon class="overwrite-icon">
                  <IconUser />
                </NIcon>
              </template>
              Manually entered
            </NTooltip>
          </div>
          <div class="detail-value">
            <gaia-extraction-field-row
              :key-name="field.key"
              :value="field.value"
              :field-meta="getFieldMeta(field.key)"
              :edit-mode="editMode"
              @update:edit-values="handleEditValuesUpdate(field.key, $event)"
            />
          </div>
        </div>
      </div>
    </NCard>
  </NModal>
</template>

<style scoped>
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 4px;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.edit-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.cell-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 0;
}

.no-extraction {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  min-height: 40px;
}

.detail-label {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  min-width: 140px;
  max-width: 140px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 8px;
  flex-shrink: 0;
}

.detail-label .label-text {
  white-space: nowrap;
  line-height: 1.5;
}

.overwrite-icon {
  font-size: 14px;
  color: #6b7280;
  opacity: 0.8;
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Ensure proper alignment for FieldDisplay content */
.detail-value :deep(.field-display) {
  width: 100%;
}

.detail-value :deep(.ai-value-container),
.detail-value :deep(.view-mode),
.detail-value :deep(.edit-mode) {
  width: 100%;
}

.detail-value :deep(.original-value-hint) {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

/* Improve input and textarea alignment */
.detail-value :deep(input),
.detail-value :deep(textarea),
.detail-value :deep(.n-input),
.detail-value :deep(.n-input-number),
.detail-value :deep(.n-select),
.detail-value :deep(.n-date-picker) {
  width: 100%;
}

/* Ensure proper spacing for field value display */
.detail-value :deep(.field-value) {
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.5;
  color: #111827;
}

.detail-value :deep(.field-value-complex) {
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Improve textarea alignment */
.detail-value :deep(textarea) {
  resize: vertical;
  min-height: 80px;
  line-height: 1.5;
  padding: 8px 12px;
}

/* Improve input alignment */
.detail-value :deep(input[type='text']),
.detail-value :deep(input[type='number']) {
  padding: 8px 12px;
  line-height: 1.5;
}

/* Ensure consistent spacing for all form elements */
.detail-value :deep(.n-input-wrapper),
.detail-value :deep(.n-input-number),
.detail-value :deep(.n-select),
.detail-value :deep(.n-date-picker) {
  width: 100%;
}

/* Better alignment for empty states */
.detail-value :deep(.empty-value) {
  color: #9ca3af;
  font-style: italic;
}
</style>
